import { useEffect, useMemo, useState } from 'react';
import { User } from 'api/models';
import { MembersAPI } from 'data/members/api';

const LRU_CAPACITY = 200;

class LRUCache<K, V> {
  private cache = new Map<K, V>();

  private readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);

    if (value === undefined) {
      return undefined;
    }

    // Move to end (most recently used)
    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  set(key: K, value: V): void {
    // If key exists, remove it first (will be re-added at end)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.capacity) {
      // Evict least recently used (first entry)
      const firstKey = this.cache.keys().next().value;

      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }
}

const userCache = new LRUCache<number, User>(LRU_CAPACITY);

/**
 * Invalidate a specific user from the SWR cache.
 * Call this when a user's data is mutated (profile update, avatar change, etc.)
 */
export const invalidateSwrUser = (userId: number): void => {
  userCache.delete(userId);
};

/**
 * Clear the entire SWR user cache.
 * Prefer invalidateSwrUser(userId) for targeted invalidation.
 */
export const clearSwrCache = (): void => {
  userCache.clear();
};

// Batching mechanism
type PendingRequest = {
  userId: number;
  resolve: (user: User | null) => void;
  reject: (error: unknown) => void;
};

let pendingRequests: PendingRequest[] = [];
let batchTimer: NodeJS.Timeout | null = null;
const BATCH_DELAY = 100; // milliseconds to wait before batching requests

const fetchBatchedUsers = async () => {
  if (pendingRequests.length === 0) {
    return;
  }

  // Get all pending requests and clear the queue
  const requestsToProcess = [...pendingRequests];

  pendingRequests = [];

  // Extract unique userIds that are not already in cache
  const userIdsToFetch = Array.from(
    new Set(
      requestsToProcess
        .map((req) => req.userId)
        .filter((id) => !userCache.has(id)),
    ),
  );

  // If all users are already cached, resolve immediately
  if (userIdsToFetch.length === 0) {
    requestsToProcess.forEach((req) => {
      req.resolve(userCache.get(req.userId) ?? null);
    });

    return;
  }

  try {
    // Make a single batched API call
    const response = await MembersAPI.fetchPaginatedMembersData({
      includedUserIds: userIdsToFetch,
    });

    // Update cache with fetched users from both possible arrays
    // When using includedUserIds, API returns users in includedUsers array
    const fetchedUsers = [
      ...(response?.includedUsers ?? []),
      ...(response?.notInProjectUsers ?? []),
    ];

    if (fetchedUsers.length) {
      fetchedUsers.forEach((user) => {
        userCache.set(user.userId, user);
      });
    }

    // Resolve all pending requests
    requestsToProcess.forEach((req) => {
      req.resolve(userCache.get(req.userId) ?? null);
    });
  } catch (error) {
    // Reject all pending requests on error
    requestsToProcess.forEach((req) => {
      req.reject(error);
    });
  }
};

const scheduleBatchFetch = () => {
  if (batchTimer) {
    clearTimeout(batchTimer);
  }

  batchTimer = setTimeout(() => {
    batchTimer = null;
    fetchBatchedUsers();
  }, BATCH_DELAY);
};

export const requestUser = (userId: number): Promise<User | null> => {
  // If already in cache, return immediately
  const cached = userCache.get(userId);

  if (cached) {
    return Promise.resolve(cached);
  }

  // Add to pending requests
  return new Promise((resolve, reject) => {
    pendingRequests.push({ userId, resolve, reject });
    scheduleBatchFetch();
  });
};

export const getUserFromSwrCache = (userId: number): User | undefined =>
  userCache.get(userId);

export const useUserSwr = (userId: number | undefined) => {
  const [user, setUser] = useState<User | null>(
    userId != null ? userCache.get(userId) ?? null : null,
  );

  useEffect(() => {
    if (!userId) {
      setUser(null);

      return undefined;
    }

    let cancelled = false;

    // If already in cache, don't fetch
    const cached = userCache.get(userId);

    if (cached) {
      setUser(cached);

      return () => {
        cancelled = true;
      };
    }

    // Clear stale data from a previous userId while fetching the new one
    setUser(null);

    // Request user (will be batched with other requests)
    requestUser(userId)
      .then((fetchedUser) => {
        if (!cancelled) {
          setUser(fetchedUser);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Memoize the user value to provide stable identity when unchanged
  return useMemo(() => user, [user]);
};

/**
 * Batch-fetch multiple users by IDs using the SWR cache.
 * Returns a Record<userId, User> map for easy lookup.
 * Uses the same batching/caching mechanism as useUserSwr.
 */
export const requestUsers = async (
  userIds: number[],
): Promise<Record<number, User>> => {
  const uniqueIds = [...new Set(userIds.filter((id) => id != null))];

  if (uniqueIds.length === 0) {
    return {};
  }

  const users = await Promise.all(uniqueIds.map((id) => requestUser(id)));
  const map: Record<number, User> = {};

  users.forEach((user) => {
    if (user) {
      map[user.userId] = user;
    }
  });

  return map;
};

export const useUsersSwr = (userIds: number[]) => {
  const [users, setUsers] = useState<User[]>(
    () => userIds.map((id) => userCache.get(id)).filter(Boolean) as User[],
  );

  const userIdsKey = useMemo(() => userIds.join(','), [userIds]);

  useEffect(() => {
    if (userIds.length === 0) {
      setUsers([]);

      return undefined;
    }

    let cancelled = false;

    Promise.all(userIds.map((id) => requestUser(id)))
      .then((results) => {
        if (!cancelled) {
          setUsers(results.filter(Boolean) as User[]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userIdsKey]);

  return useMemo(() => users, [users]);
};
